/**
 * Input component overrides.
 * Dark inputs with glowing focus borders.
 */

import type { Theme } from '@mui/material/styles';

const input: Theme['components'] = {
  MuiFormControl: {
    styleOverrides: {
      root: {
        '&:has(.MuiRadio-root) .MuiFormHelperText-root, &:has(.MuiCheckbox-root) .MuiFormHelperText-root, &:has(.MuiSwitch-root) .MuiFormHelperText-root':
          {
            marginInline: 0,
          },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        lineHeight: 1.6,
        '&.MuiInput-underline': {
          '&:before': {
            borderColor: 'var(--mui-palette-customColors-inputBorder)',
          },
          '&:not(.Mui-disabled, .Mui-error):hover:before': {
            borderColor: 'var(--mui-palette-action-active)',
          },
        },
        '&.Mui-disabled .MuiInputAdornment-root, &.Mui-disabled .MuiInputAdornment-root > *': {
          color: 'var(--mui-palette-action-disabled)',
        },
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        borderStartStartRadius: 'var(--mui-shape-customBorderRadius-sm)',
        borderStartEndRadius: 'var(--mui-shape-customBorderRadius-sm)',
        '&:before': {
          borderBottom: '1px solid var(--mui-palette-text-secondary)',
        },
        '&:hover:before': {
          borderBottom: '1px solid var(--mui-palette-text-primary)',
        },
        '&.Mui-disabled:before': {
          borderBottomStyle: 'solid',
          opacity: 0.38,
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      shrink: ({ ownerState }) => ({
        ...(ownerState.variant === 'outlined' && {
          transform: 'translate(14px, -8px) scale(0.867)',
        }),
        ...(ownerState.variant === 'filled' && {
          transform: `translate(12px, ${ownerState.size === 'small' ? 4 : 7}px) scale(0.867)`,
        }),
        ...(ownerState.variant === 'standard' && {
          transform: 'translate(0, -1.5px) scale(0.867)',
        }),
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: 'var(--color-bg-surface)',
        borderRadius: '8px',
        transition: 'box-shadow 120ms ease, border-color 120ms ease',
        '&:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--color-border-strong, var(--color-border-default))',
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--color-border-default)',
        },
        /* Site / CoordioDropdown open-trigger ring */
        '&:not(.Mui-error).Mui-focused': {
          boxShadow: '0 0 0 2px var(--color-accent-subtle)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: '1px',
          borderColor: 'var(--color-accent-primary)',
        },
      },
      input: ({ theme, ownerState }) => ({
        ...(ownerState?.size === 'medium' && {
          '&:not(.MuiInputBase-inputMultiline, .MuiInputBase-inputAdornedStart)': {
            padding: theme.spacing(3.5, 4),
          },
          height: '1.5em',
        }),
        '& ~ .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--color-border-default)',
        },
      }),
      notchedOutline: {
        borderColor: 'var(--color-border-default)',
        transition: 'border-color 120ms ease',
        '& legend': {
          fontSize: '0.867em',
        },
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: {
        color: 'var(--mui-palette-text-secondary)',
        '& i, & svg': {
          fontSize: '1rem !important',
        },
        '& *': {
          color: 'inherit !important',
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        lineHeight: 1.2,
        letterSpacing: 'unset',
        marginTop: 6,
      },
    },
  },
};

export default input;
